import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { SupportService } from '@/app/services/support/support.service';
import {
  SupportAlertPreferences,
  SupportAttachment,
  SupportTicketDetail,
} from '@/app/common/general-types/support-ticket';
import { supportTicketCategoryLabel } from '@/app/common/support-ticket-category.util';
import { SupportAttachmentInputComponent } from '@/app/molecules/support-attachment-input/support-attachment-input.component';
import { SupportAttachmentsDisplayComponent } from '@/app/molecules/support-attachments-display/support-attachments-display.component';
import { SupportAlertPreferencesInputComponent } from '@/app/molecules/support-alert-preferences-input/support-alert-preferences-input.component';

@Component({
  selector: 'app-support-ticket-thread',
  imports: [
    FormsModule,
    DatePipe,
    SupportAttachmentInputComponent,
    SupportAttachmentsDisplayComponent,
    SupportAlertPreferencesInputComponent,
  ],
  templateUrl: './support-ticket-thread.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportTicketThreadComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly supportService = inject(SupportService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly pollIntervalMs = 8000;
  private pendingScrollBehavior: ScrollBehavior | null = null;

  subscriptionList = new Subscription();

  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLElement>;
  @ViewChild('messagesEnd') private messagesEnd?: ElementRef<HTMLElement>;

  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly error = signal('');
  readonly showThankYou = signal(false);
  readonly showPreferenceEditor = signal(false);
  readonly savingPreferences = signal(false);
  readonly preferenceError = signal('');
  readonly preferenceSuccess = signal('');
  readonly ticket = signal<SupportTicketDetail | null>(null);
  readonly accessToken = signal<string | null>(null);
  readonly ticketId = signal<string | null>(null);

  reply = '';
  attachments: SupportAttachment[] = [];
  alertPreferences: SupportAlertPreferences = {
    sms: false,
    whatsapp: false,
    email: false,
    push: false,
  };

  get threadViewportHeight(): string {
    return this.accessToken() ? '100dvh' : 'calc(100vh - 4rem)';
  }

  ngOnInit(): void {
    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => {
        const isMagic = this.isMagicLinkRoute();
        const token =
          params.get('token') ??
          (isMagic ? params.get('id') : null);
        this.accessToken.set(token);
        this.ticketId.set(isMagic ? null : params.get('id'));
        this.loadTicket();
      }),
    );

    this.subscriptionList.add(
      this.route.parent?.paramMap.subscribe((params) => {
        const parentToken = params.get('token');
        if (parentToken && !this.accessToken()) {
          this.accessToken.set(parentToken);
          this.loadTicket();
        }
      }) ?? Subscription.EMPTY,
    );

    this.subscriptionList.add(
      this.route.queryParamMap.subscribe((query) => {
        if (query.get('submitted') === '1') {
          this.showThankYou.set(true);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        }
      }),
    );

    this.subscriptionList.add(
      interval(this.pollIntervalMs).subscribe(() => this.pollTicket()),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  private isMagicLinkRoute(): boolean {
    return this.router.url.includes('/access');
  }

  private pollTicket(): void {
    if (
      document.visibilityState !== 'visible' ||
      this.submitting() ||
      this.savingPreferences() ||
      this.showPreferenceEditor() ||
      (!this.accessToken() && !this.ticketId())
    ) {
      return;
    }

    this.loadTicket({ silent: true });
  }

  loadTicket(options?: { silent?: boolean }): void {
    const token = this.accessToken();
    const id = this.ticketId();

    if (!token && !id) {
      return;
    }

    if (!options?.silent) {
      this.loading.set(true);
      this.error.set('');
    }

    const request = token
      ? this.supportService.getTicketByToken(token)
      : this.supportService.getTicket(id!);

    this.subscriptionList.add(
      request.subscribe({
        next: (ticket) => {
          const stickToBottom = !options?.silent || this.isNearBottom();
          this.ticket.set(ticket);
          if (!this.showPreferenceEditor()) {
            this.syncAlertPreferences(ticket.alertPreferences);
          }
          if (!options?.silent) {
            this.loading.set(false);
          }
          if (stickToBottom) {
            this.queueScrollToLatest(options?.silent ? 'smooth' : 'auto');
          }
          this.cdr.markForCheck();
        },
        error: () => {
          if (!options?.silent) {
            this.ticket.set(null);
            this.loading.set(false);
            this.error.set('Ticket not found.');
            this.cdr.markForCheck();
          }
        },
      }),
    );
  }

  private queueScrollToLatest(behavior: ScrollBehavior = 'auto'): void {
    this.pendingScrollBehavior = behavior;
    setTimeout(() => this.flushScrollToLatest(), 0);
  }

  private flushScrollToLatest(): void {
    const behavior = this.pendingScrollBehavior;
    if (!behavior) {
      return;
    }
    this.pendingScrollBehavior = null;

    const end = this.messagesEnd?.nativeElement;
    const container = this.messagesContainer?.nativeElement;
    if (!end || !container) {
      return;
    }

    end.scrollIntoView({ behavior, block: 'end' });
    if (behavior === 'auto') {
      container.scrollTop = container.scrollHeight;
    }
  }

  private isNearBottom(): boolean {
    const container = this.messagesContainer?.nativeElement;
    if (!container) {
      return true;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom < 120;
  }

  onAttachmentsChange(attachments: SupportAttachment[]): void {
    this.attachments = attachments;
  }

  onAlertPreferencesChange(prefs: SupportAlertPreferences): void {
    this.alertPreferences = prefs;
  }

  onMobileSaved(mobile: string): void {
    const current = this.ticket();
    if (!current?.user) {
      return;
    }

    this.ticket.set({
      ...current,
      user: {
        ...current.user,
        mobile,
      },
    });
    this.cdr.markForCheck();
  }

  sendReply(): void {
    const body = this.reply.trim();
    const current = this.ticket();
    if ((!body && this.attachments.length === 0) || !current) {
      return;
    }

    this.submitting.set(true);
    this.error.set('');

    const attachments = this.attachments.length ? this.attachments : undefined;
    const token = this.accessToken();
    const request = token
      ? this.supportService.addMessageByToken(token, body, attachments)
      : this.supportService.addUserMessage(current.id, body, attachments);

    this.subscriptionList.add(
      request.subscribe({
        next: (ticket) => {
          this.ticket.set(ticket);
          this.reply = '';
          this.attachments = [];
          this.submitting.set(false);
          this.showThankYou.set(true);
          this.queueScrollToLatest('smooth');
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not send your reply.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  closeTicket(): void {
    const current = this.ticket();
    if (!current) {
      return;
    }

    this.submitting.set(true);
    const token = this.accessToken();
    const request = token
      ? this.supportService.closeTicketByToken(token)
      : this.supportService.closeUserTicket(current.id);

    this.subscriptionList.add(
      request.subscribe({
        next: (ticket) => {
          this.ticket.set(ticket);
          this.submitting.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('Could not close this ticket.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  goBack(): void {
    if (this.accessToken()) {
      return;
    }
    this.router.navigate(['/support']);
  }

  isClosed(): boolean {
    return this.ticket()?.status === 'closed';
  }

  canSendReply(): boolean {
    return !!this.reply.trim() || this.attachments.length > 0;
  }

  dismissThankYou(): void {
    this.showThankYou.set(false);
  }

  alertPreferencePills(
    preferences?: SupportAlertPreferences | null,
  ): { label: string; className: string }[] {
    if (!preferences) {
      return [];
    }

    const pills: { label: string; className: string }[] = [];
    if (preferences.sms) {
      pills.push({ label: 'SMS', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' });
    }
    if (preferences.whatsapp) {
      pills.push({ label: 'WhatsApp', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' });
    }
    if (preferences.email) {
      pills.push({ label: 'Email', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' });
    }
    if (preferences.push) {
      pills.push({ label: 'Push', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' });
    }
    return pills;
  }

  replyComposerTitle(): string {
    return this.isClosed() ? 'Reopen with a reply' : 'Add a reply';
  }

  replyButtonLabel(): string {
    if (this.submitting()) {
      return 'Sending...';
    }
    return this.isClosed() ? 'Send and reopen' : 'Send reply';
  }

  syncAlertPreferences(preferences?: SupportAlertPreferences | null): void {
    this.alertPreferences = {
      sms: preferences?.sms ?? false,
      whatsapp: preferences?.whatsapp ?? false,
      email: preferences?.email ?? false,
      push: preferences?.push ?? false,
    };
  }

  hasAlertPreference(): boolean {
    return (
      this.alertPreferences.sms ||
      this.alertPreferences.whatsapp ||
      this.alertPreferences.email ||
      this.alertPreferences.push
    );
  }

  openPreferenceEditor(): void {
    this.syncAlertPreferences(this.ticket()?.alertPreferences);
    this.showPreferenceEditor.set(true);
    this.preferenceError.set('');
    this.preferenceSuccess.set('');
  }

  cancelPreferenceEdit(): void {
    this.syncAlertPreferences(this.ticket()?.alertPreferences);
    this.showPreferenceEditor.set(false);
    this.preferenceError.set('');
  }

  saveAlertPreferences(): void {
    const current = this.ticket();
    if (!current || !this.hasAlertPreference()) {
      this.preferenceError.set('Select at least one way to be notified about replies.');
      return;
    }

    this.savingPreferences.set(true);
    this.preferenceError.set('');
    this.preferenceSuccess.set('');

    const token = this.accessToken();
    const request = token
      ? this.supportService.updateAlertPreferencesByToken(token, this.alertPreferences)
      : this.supportService.updateUserAlertPreferences(current.id, this.alertPreferences);

    this.subscriptionList.add(
      request.subscribe({
        next: (ticket) => {
          this.ticket.set(ticket);
          this.syncAlertPreferences(ticket.alertPreferences);
          this.showPreferenceEditor.set(false);
          this.savingPreferences.set(false);
          this.preferenceSuccess.set('Notification preferences updated.');
          this.cdr.markForCheck();
        },
        error: () => {
          this.savingPreferences.set(false);
          this.preferenceError.set('Could not update notification preferences.');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  categoryLabel(): string {
    return supportTicketCategoryLabel(this.ticket()?.category);
  }

  statusClass(status: string): string {
    return status === 'open'
      ? 'bg-green-50 text-green-700 ring-1 ring-green-100 dark:bg-green-900/30 dark:text-green-300'
      : 'bg-gray-100 text-gray-600 dark:bg-zinc-700 dark:text-zinc-300';
  }

  messageRowClass(authorType: string): string {
    return authorType === 'admin' ? 'justify-start' : 'justify-end';
  }

  messageBubbleClass(authorType: string): string {
    return authorType === 'admin'
      ? 'rounded-tl-md border border-indigo-100 dark:border-indigo-800 bg-white dark:bg-zinc-800'
      : 'rounded-tr-md border border-blue-100 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40';
  }

  messageAuthorClass(authorType: string): string {
    return authorType === 'admin' ? 'text-indigo-700 dark:text-indigo-300' : 'text-primary';
  }
}
